/**
 * Column picker popover for the CreativesTable.
 * Shows grouped checkboxes: Core (disabled, always on), KPIs, Dimensions, Gates.
 *
 * FR-71: Grouped column visibility toggles
 */

import type { VisibilityState } from '@tanstack/react-table';
import { Columns } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';

import { cn } from '@/lib/utils';


import type { ColumnGroup } from './CreativesTable';

// ============================================================================
// TYPES
// ============================================================================

export interface ColumnPickerProps {
  columnGroups: ColumnGroup[];
  columnVisibility: VisibilityState;
  onToggleColumn: (colId: string) => void;
}

// ============================================================================
// COMPONENT
// ============================================================================

export function ColumnPicker({
  columnGroups,
  columnVisibility,
  onToggleColumn,
}: ColumnPickerProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'flex items-center gap-1 text-xs px-2.5 py-1 rounded border transition-colors',
          open
            ? 'bg-brand text-white border-brand'
            : 'bg-surface border-border-subtle text-secondary hover:bg-surface-subtle'
        )}
      >
        <Columns size={12} />
        Columns
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 z-20 w-64 bg-surface rounded-[10px] shadow-lg border border-border-subtle overflow-hidden">
          <div className="px-3 py-2 border-b border-border-subtle bg-canvas">
            <div className="text-xs font-semibold text-primary">Configure Columns</div>
            <div className="text-[10px] text-muted">Always visible: Name, Type, Verdict</div>
          </div>
          <div className="max-h-72 overflow-y-auto">
            {columnGroups.map((group) => {
              const toggleable = group.columns.filter((c) => !c.alwaysOn);
              if (toggleable.length === 0) return null;
              return (
                <div key={group.category}>
                  <div className="px-3 py-1.5 text-[10px] font-semibold text-muted uppercase tracking-wide bg-canvas">
                    {group.category}
                  </div>
                  {toggleable.map((col) => (
                    <label
                      key={col.id}
                      className="flex items-center gap-2 px-3 py-1.5 hover:bg-surface-subtle cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={columnVisibility[col.id] !== false}
                        onChange={() => onToggleColumn(col.id)}
                        className="accent-brand"
                      />
                      <span className="text-xs text-primary">{col.label}</span>
                    </label>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
